import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { t } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { Icon, type IconName } from './Icon.tsx';
import { GROUPS, type NavEntry } from './nav.tsx';
import { hitHint, quickCreates, RECORD_KINDS, type SearchHit } from './search.ts';

/**
 * Search and jump (Ctrl K): type a few letters and press Enter. It finds places in the app,
 * the business's records the person may open (customers, invoices, products, people…, asked
 * from the server as they type), things to create and a few commands. Results are grouped, the
 * arrow keys move, Escape closes.
 */

interface Hit {
  key: string;
  icon: IconName;
  label: string;
  hint: string;
  section: string;
  run: () => void;
}

const fold = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

export function CommandPalette({ entries, onClose }: { entries: NavEntry[]; onClose: () => void }) {
  const navigate = useNavigate();
  const { setTheme, theme } = useUi();
  const { can } = useSession();
  const [q, setQ] = useState('');
  const [at, setAt] = useState(0);
  const records = useRecordSearch(q);
  const list = useRef<HTMLDivElement>(null);
  const prev = useRef<HTMLElement | null>(null);
  useEffect(() => {
    prev.current = document.activeElement as HTMLElement | null;
    return () => prev.current?.focus?.();
  }, []);

  const hits = useMemo(() => {
    const go = (to: string) => () => {
      onClose();
      navigate(to);
    };
    const places: Hit[] = entries.map((e) => ({
      key: e.to,
      icon: e.icon,
      label: e.label,
      hint: t(GROUPS.find((g) => g.key === e.group)!.label),
      section: t('shell.s_places'),
      run: go(e.to),
    }));
    const records_: Hit[] = (records.hits ?? []).flatMap((h) => {
      const kind = RECORD_KINDS[h.kind];
      const to = kind?.link(h, can);
      return kind && to ? [{ key: `${h.kind}:${h.id}`, icon: kind.icon, label: h.title || hitHint(h), hint: hitHint(h), section: t(kind.label), run: go(to) }] : [];
    });
    const creates: Hit[] = quickCreates(can).map((c) => ({ key: `new:${c.key}`, icon: 'plus', label: c.label, hint: t('qc.title'), section: t('shell.s_create'), run: go(c.to) }));
    const commands: Hit[] = [
      { key: 'apps', icon: 'apps', label: t('shell.apps'), hint: t('shell.s_places'), section: t('shell.s_commands'), run: go('/apps') },
      {
        key: 'theme',
        icon: theme === 'dark' ? 'sun' : 'moon',
        label: theme === 'dark' ? t('shell.theme_to_light') : t('shell.theme_to_dark'),
        hint: t('shell.theme'),
        section: t('shell.s_commands'),
        run: () => {
          setTheme(theme === 'dark' ? 'light' : 'dark');
          onClose();
        },
      },
    ];
    const words = fold(q).split(/\s+/).filter(Boolean);
    const match = (h: Hit) => words.every((w) => fold(`${h.label} ${h.hint}`).includes(w));
    if (!words.length) return [...places.slice(0, 6), ...creates, ...commands];
    // Places whose name starts with what was typed come first.
    const found = places.filter(match).sort((a, b) => Number(!fold(a.label).startsWith(words[0]!)) - Number(!fold(b.label).startsWith(words[0]!)));
    return [...found.slice(0, 6), ...records_, ...creates.filter(match), ...commands.filter(match)];
  }, [q, entries, navigate, onClose, setTheme, theme, records.hits, can]);

  useEffect(() => setAt(0), [q]);
  useEffect(() => {
    list.current?.querySelector<HTMLElement>(`[data-i="${at}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [at]);

  let section = '';
  return (
    <div className="overlay cmd-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="cmd" role="dialog" aria-modal="true" aria-label={t('shell.search')}>
        <div className="cmd-input">
          <Icon name="search" size={20} />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('shell.search_placeholder')}
            aria-label={t('shell.search')}
            role="combobox"
            aria-expanded="true"
            aria-controls="cmd-list"
            aria-activedescendant={hits[at] ? `cmd-${at}` : undefined}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setAt((i) => Math.min(hits.length - 1, i + 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setAt((i) => Math.max(0, i - 1));
              } else if (e.key === 'Enter') {
                hits[at]?.run();
              } else if (e.key === 'Escape') {
                onClose();
              }
            }}
          />
          <kbd className="topbar-kbd">Esc</kbd>
        </div>
        <div className="cmd-list" id="cmd-list" role="listbox" ref={list} aria-label={t('shell.search')}>
          {hits.length ? (
            hits.map((h, i) => {
              const head = h.section !== section ? h.section : null;
              section = h.section;
              return (
                <div key={h.key}>
                  {head ? <p className="cmd-section">{head}</p> : null}
                  <div id={`cmd-${i}`} data-i={i} role="option" aria-selected={i === at} className={`cmd-item${i === at ? ' on' : ''}`} onMouseMove={() => setAt(i)} onClick={() => h.run()}>
                    <span className="cmd-icon">
                      <Icon name={h.icon} size={18} />
                    </span>
                    <span className="cmd-label">{h.label}</span>
                    <span className="cmd-hint">{h.hint}</span>
                    {i === at ? <Icon name="chevronRight" size={16} /> : null}
                  </div>
                </div>
              );
            })
          ) : records.busy ? null : (
            <p className="cmd-empty">{t('shell.search_none', { q })}</p>
          )}
          {records.busy ? (
            <p className="cmd-status" role="status">
              <span className="spinner sm" aria-hidden="true" />
              {t('srch.searching')}
            </p>
          ) : records.failed ? (
            <p className="cmd-status" role="status">
              {t('srch.failed')}
            </p>
          ) : null}
        </div>
        <div className="cmd-foot">
          <span>
            <kbd>↑</kbd> <kbd>↓</kbd> {t('shell.key_move_short')}
          </span>
          <span>
            <kbd>↵</kbd> {t('shell.key_open')}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Asks the server for records as the person types (from two letters, after a short pause). */
function useRecordSearch(q: string) {
  const [state, setState] = useState<{ q: string; hits: SearchHit[] | null; busy: boolean; failed: boolean }>({ q: '', hits: null, busy: false, failed: false });
  useEffect(() => {
    const text = q.trim();
    if (text.length < 2) {
      setState({ q: text, hits: null, busy: false, failed: false });
      return;
    }
    let live = true;
    setState((s) => ({ ...s, busy: true, failed: false }));
    const timer = setTimeout(() => {
      action<{ hits: SearchHit[] }>('search.find', { q: text }).then(
        (r) => live && setState({ q: text, hits: r.hits, busy: false, failed: false }),
        () => live && setState({ q: text, hits: null, busy: false, failed: true }),
      );
    }, 220);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [q]);
  return state;
}
